import React from "react";
import {
  ArrowLeftRight,
  ArrowRight,
  ArrowUpRight,
  FastForward,
  MapPin,
  MapPinned,
  RefreshCw,
  Route,
} from "lucide-react";
import { Link } from "react-router-dom";

const views = [
  {
    Icon: MapPinned,
    label: "Activity",
    title: "Where activity builds",
    body: "Compare how many people are present across areas and time periods.",
  },
  {
    Icon: Route,
    label: "Journeys",
    title: "How places connect",
    body: "Understand origins, destinations and movement between defined areas.",
  },
  {
    Icon: RefreshCw,
    label: "Change",
    title: "What changes a pattern",
    body: "Measure the effect of events, road works, new services and development.",
  },
];

const products = [
  {
    Icon: MapPin,
    tag: "Locations",
    title: "Activities",
    body: "Location-based activity patterns",
  },
  {
    Icon: Route,
    tag: "Trips",
    title: "Origin to Destination",
    body: "Movement between selected areas",
  },
  {
    Icon: FastForward,
    tag: "Live",
    title: "Real Time",
    body: "Live monitoring dashboards",
    featured: true,
  },
  {
    Icon: ArrowLeftRight,
    tag: "Network",
    title: "Flow",
    body: "Traffic flow on road links",
  },
];

const ProductPreview = () => (
  <div className="overflow-hidden rounded-2xl border border-paper-300 bg-paper shadow-[0_24px_70px_rgba(23,34,29,0.12)]" aria-label="Preview of Telcofy's mobility products">
    <div className="flex items-center justify-between border-b border-paper-300 bg-white px-5 py-4">
      <div className="flex items-center gap-2.5">
        <img src="/brand/telcofy-mark.svg" alt="" className="h-7 w-7" />
        <span className="font-display text-lg font-bold tracking-tight text-ink">
          Telco<span className="text-brand-600">fy</span>
        </span>
      </div>
      <span className="rounded-full bg-brand-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-700">
        CityOS
      </span>
    </div>

    <div className="bg-paper-100 p-5 sm:p-7">
      <div className="mb-5">
        <p className="mb-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-ink-faint">Workbench</p>
        <h3 className="font-display text-2xl font-bold tracking-tight text-ink">Select a Product</h3>
        <p className="mt-1 text-xs text-ink-muted">Choose an analytics tool to get started.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {products.map(({ Icon, tag, title, body, featured }) => (
          <div
            key={title}
            className={`group relative min-h-[154px] rounded-xl border bg-white p-4 ${
              featured
                ? "border-brand-500 shadow-[0_12px_30px_rgba(39,125,73,0.12)]"
                : "border-paper-300"
            }`}
          >
            <div className="mb-5 flex items-start justify-between gap-2">
              <div className={`grid h-9 w-9 place-items-center rounded-lg ${featured ? "bg-brand-600 text-white" : "bg-brand-50 text-brand-700"}`}>
                <Icon size={18} />
              </div>
              <span className="text-[8px] font-semibold uppercase tracking-[0.13em] text-ink-faint">{tag}</span>
            </div>
            <p className="font-display text-sm font-bold leading-tight text-ink">{title}</p>
            <p className="mt-1 text-[10px] leading-snug text-ink-muted">{body}</p>
            {featured && (
              <div className="absolute bottom-3 right-3 grid h-6 w-6 place-items-center rounded-full bg-brand-50 text-brand-700">
                <ArrowRight size={12} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const HumanMobilitySection = () => (
  <section id="human-mobility" className="py-24 bg-paper-200 border-y border-paper-300">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-12 gap-10 xl:gap-16 items-center mb-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold tracking-widest text-brand-600 uppercase mb-3">Human mobility</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-ink tracking-tight leading-tight">
            A practical view of activity, journeys and change.
          </h2>
          <p className="mt-6 text-base md:text-lg text-ink-muted leading-relaxed">
            Telcofy turns movement data into maps and measures that help teams understand a place, prepare for change and review what happened.
          </p>
        </div>
        <div className="lg:col-span-7">
          <ProductPreview />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {views.map(({ Icon, label, title, body }) => (
          <article key={label} className="bg-paper border border-paper-300 rounded-xl p-7 min-h-[250px] flex flex-col">
            <div className="w-11 h-11 rounded-lg bg-brand-50 grid place-items-center mb-10">
              <Icon size={21} className="text-brand-700" />
            </div>
            <p className="text-[11px] font-semibold tracking-widest text-brand-700 uppercase mb-2">{label}</p>
            <h3 className="font-display text-2xl font-semibold text-ink mb-3">{title}</h3>
            <p className="text-sm text-ink-muted leading-relaxed">{body}</p>
          </article>
        ))}
      </div>

      <div className="mt-5 flex flex-col md:flex-row md:items-center justify-between gap-5 rounded-xl bg-[#17221d] px-7 py-6">
        <p className="m-0 text-sm md:text-base text-[#c4d0c9] leading-relaxed max-w-3xl">
          The platform handles data processing, privacy controls and quality checks behind these views.
        </p>
        <Link to="/platform" className="inline-flex items-center gap-2 text-sm font-semibold text-[#8add8d] whitespace-nowrap">
          Platform overview <ArrowUpRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);

export default HumanMobilitySection;
